import { Injectable } from "@angular/core";

/**
 * Cache di sessione per la lista licenze del cliente (/questionari).
 * Il componente viene distrutto/ricreato a ogni navigazione (es. rientro da
 * /valutazioni) e rifaceva da zero query licenze + fetch questionario/versione:
 * qui sopravvivono i dati elaborati, così il rientro dipinge subito dalla cache
 * mentre il refresh avviene in background. FireService è congelato (DO NOT
 * TOUCH): lo stato nuovo vive qui.
 */
@Injectable({ providedIn: "root" })
export class LicenzeDataService {
  /**
   * Finestra entro cui il paint-from-cache è accettabile. Oltre la soglia si
   * preferisce lo spinner al dato vecchio: il servizio vive nell'injector root,
   * quindi senza TTL una tab lasciata aperta per ore ripartiva sempre dalla
   * lista del primo caricamento.
   */
  private static readonly TTL_MS = 60_000;

  // azienda → lista licenze già elaborate (quest/vers/utilizzate risolti)
  private licenzeByAzienda = new Map<string, any[]>();
  // azienda → timestamp dell'ultimo setLicenze, per il TTL
  private savedAt = new Map<string, number>();

  // Doc questionario/versione condivisi tra licenze: i doc versione contengono
  // l'INTERO questionario, ri-scaricarli a ogni navigazione è il costo maggiore
  // del fallback per le licenze senza titoli denormalizzati.
  readonly questCache = new Map<string, any>();
  readonly versCache = new Map<string, any>();

  getLicenze(azienda: string): any[] | undefined {
    const at = this.savedAt.get(azienda);
    if (at == null || Date.now() - at > LicenzeDataService.TTL_MS) {
      return undefined;
    }
    return this.licenzeByAzienda.get(azienda);
  }

  setLicenze(azienda: string, licenze: any[]): void {
    this.licenzeByAzienda.set(azienda, licenze);
    this.savedAt.set(azienda, Date.now());
  }

  /**
   * Svuota tutto. Chiamato al logout: il servizio vive nell'injector root e la
   * SPA non ricarica la pagina, quindi senza questo un secondo login nella
   * stessa tab (anche di un altro utente) ripartiva dai dati del precedente.
   * Include `questCache`/`versCache`: i titoli questionario/versione altrimenti
   * restavano quelli del primo caricamento per tutta la vita della tab.
   */
  clear(): void {
    this.licenzeByAzienda.clear();
    this.savedAt.clear();
    this.questCache.clear();
    this.versCache.clear();
  }
}
